import { useSetFlowSessionName } from "@/api/flow_sessions";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { InboxItem } from "@cohesive/api-client";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

export function RenameChatDialog({
  item,
  onClose,
  onRenamed,
}: {
  item: InboxItem | null;
  /** Unused — cache invalidation now lives in useSetFlowSessionName.
   *  Kept optional so existing call sites don't need to change. */
  workspaceId?: string;
  onClose: () => void;
  onRenamed?: (name: string) => void;
}) {
  const [name, setName] = useState("");

  useEffect(() => {
    if (item) setName(item.flow_session?.name || "");
  }, [item?.id]);

  // useSetFlowSessionName invalidates the flowSession + all
  // workspace-flow-sessions (workspace + recents) caches, then chains
  // this onSuccess so the dialog closes and toasts on completion.
  const setFlowSessionName = useSetFlowSessionName({
    onSuccess: (updatedSession) => {
      toast.success("Chat renamed");
      onRenamed?.(updatedSession.name);
      onClose();
    },
    onError: () => toast.error("Failed to rename chat"),
  });

  const handleSubmit = () => {
    if (!item || !name.trim()) return;
    setFlowSessionName.mutate({
      flowSessionId: item.flow_session_id,
      name: name.trim(),
    });
  };

  return (
    <Dialog
      open={item !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename chat</DialogTitle>
          <DialogDescription>Enter a new name for this chat.</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Chat name"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || setFlowSessionName.isPending}
          >
            {setFlowSessionName.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
